import type {
  Approval,
  ApprovalDecisionState,
  ApprovalInboxItem,
  ApprovalTokenDecision,
  Page,
} from '@servicetim/shared';
import { useMutation } from '@tanstack/react-query';
import { portalPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

/*
 * Approvals (`/v1/approvals`, `/v1/tickets/{id}/approvals/{aid}/approve|reject`; S-07-09,
 * S-07-10). Session-based; shared by the admin ticket detail panel, the admin approvals inbox
 * and the portal approvals inbox — all authenticated agents/customers hit the same `/v1` routes.
 */

export interface ApprovalFilters {
  /** Default `true`: only the caller's own approver rows (there is no "everyone's" inbox view). */
  mine?: boolean;
  decision?: ApprovalDecisionState;
}

/** `GET /v1/approvals?mine=&decision=` (default `mine=true&decision=pending`). */
export function useApprovals(
  filters: ApprovalFilters = {},
  pageSize = DEFAULT_PAGE_SIZE,
  options: { enabled?: boolean } = {},
) {
  const { request, keys } = useApiContext();
  return usePagedList<ApprovalInboxItem>({
    queryKey: keys.approvals.list({ ...filters, pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<ApprovalInboxItem>>('GET', portalPath('/approvals'), {
        query: { ...filters, ...pageQuery(cursor, pageSize) },
        signal,
      }),
    enabled: options.enabled,
  });
}

export interface DecideApprovalInput {
  ticketId: string;
  approvalId: string;
  decision: Extract<ApprovalDecisionState, 'approved' | 'rejected'>;
  comment?: string;
}

export interface DecideApprovalResult {
  id: string;
  decision: ApprovalDecisionState;
  requestOutcome: ApprovalDecisionState;
}

/** `POST /v1/tickets/{id}/approvals/{aid}/approve` or `.../reject` (session of the approver). */
export function useDecideApproval() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ ticketId, approvalId, decision, comment }: DecideApprovalInput) =>
      request<DecideApprovalResult>(
        'POST',
        portalPath(
          `/tickets/${ticketId}/approvals/${approvalId}/${decision === 'approved' ? 'approve' : 'reject'}`,
        ),
        { body: comment === undefined ? {} : { comment } },
      ),
    onSuccess: (_result, { ticketId }) =>
      invalidate(keys.approvals.all, keys.ticket(ticketId).sla, keys.ticket(ticketId).detail),
  });
}

/**
 * `POST /v1/approvals/decide` — the signed, single-use email-link decision (S-07-10; ADR-0019
 * §3: "the decision endpoint accepts the token or a session"). **Backend gap**: no route for
 * this exists yet in `apps/api/src/portal/approvals.ts` (only the session-based approve/reject
 * above is mounted) — this hook is written to the shape `ApprovalTokenDecisionSchema` describes
 * so the portal token page is ready the moment the endpoint lands; until then every call 404s
 * and the page shows a "not available yet" state instead of crashing.
 */
export function useDecideApprovalByToken() {
  const { request } = useApiContext();
  return useMutation({
    mutationFn: (input: ApprovalTokenDecision) =>
      request<{ decision: ApprovalDecisionState }>('POST', portalPath('/approvals/decide'), {
        body: input,
      }),
  });
}

export type { Approval };
